import type {
  PayrollBatchItem,
  PayrollBatchRow,
} from '@/app/features/employee/emp-payroll';
import { toAmount } from '../shared/payroll.helpers';

/**
 * Reading a batch row. The API spreads a row's pay over several fields and
 * breakdown objects; these helpers are the one place that knows where each
 * number lives.
 */

/** Once a row's salary sheet is generated its amounts are final. */
export const isSheetGenerated = (row?: { salary_sheet?: string } | null) =>
  row?.salary_sheet === 'GENERATED';

/**
 * A batch can be removed while nothing in it is final: it failed, or no
 * employee's salary sheet has been generated yet.
 */
export const canDeleteBatch = (batch: PayrollBatchItem) =>
  batch.status === 'FAILED' ||
  (batch.salary_sheet === 'NOT_GENERATED' && !batch.generated_salary_sheets);

/** The amounts shown on a row, all as numbers. */
export const rowAmounts = (row: PayrollBatchRow) => ({
  /**
   * Pay before any adjustment: structure plus grade benefits, or days × rate
   * when the server sends a dedicated daily breakdown.
   */
  gross: row.daily_pay_breakdown
    ? toAmount(row.daily_pay_breakdown.amount)
    : toAmount(row.total_base_salary_structure_amount) +
      toAmount(row.total_grade_benefits_amount),
  addition: toAmount(row.fixed_addition?.admin_bonus),
  deduction: toAmount(row.fixed_deduction?.admin_deduction),
  tax: toAmount(row.tax_amount),
  net: toAmount(row.net_salary),
});

export type RowAdjustments = {
  addition: number;
  deduction: number;
  tax: number;
};

/**
 * What the final salary becomes after an adjustment. Only the difference is
 * applied to the server's figure, so parts this screen does not show (overtime,
 * policy bonuses, encashment) stay exactly as the server counted them.
 */
export const projectNetSalary = (
  row: PayrollBatchRow,
  next: RowAdjustments,
) => {
  const current = rowAmounts(row);
  return (
    current.net +
    (next.addition - current.addition) -
    (next.deduction - current.deduction) -
    (next.tax - current.tax)
  );
};

/**
 * Days × rate for a daily row. The server puts these facts in the structure
 * breakdown; a dedicated `daily_pay_breakdown` wins if it is ever sent.
 */
export const dailyPayOf = (row: PayrollBatchRow) => {
  if (row.daily_pay_breakdown) {
    return {
      payableDays: toAmount(row.daily_pay_breakdown.payable_days),
      dailyRate: toAmount(row.daily_pay_breakdown.daily_rate),
    };
  }
  const breakdown = row.base_salary_structure_breakdown;
  if (breakdown?.daily_rate == null) return null;
  return {
    payableDays: toAmount(breakdown.present_days),
    dailyRate: toAmount(breakdown.daily_rate),
  };
};
